import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GalleryManager } from "@/components/GalleryManager";
import { VslManager } from "@/components/admin/VslManager";
import { ShowcaseManager } from "@/components/admin/ShowcaseManager";
import { NotificationSettings } from "@/components/admin/NotificationSettings";

const Section = ({ title, children }) => (
  <div className="bg-white border border-gray-200 p-6 sm:p-8">
    <h2 className="font-display text-xl mb-6">{title}</h2>
    {children}
  </div>
);

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    {children}
  </div>
);

const SIZES = ["small", "medium", "large", "multi"];
const SIZE_LABELS = { small: "Small", medium: "Medium", large: "Large", multi: "Multi-Piece" };

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(errText(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = s;
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <AdminLayout title="Settings"><p className="text-gray-400">Loading...</p></AdminLayout>;

  const dollars = (cents) => (cents / 100).toFixed(2);
  const toCents = (v) => Math.round(parseFloat(v || 0) * 100);
  const linesToArr = (v) => v.split("\n").map((x) => x.trim()).filter(Boolean);

  return (
    <AdminLayout title="Admin Settings">
      <div className="space-y-8 max-w-4xl">
        <Section title="Lead Notifications">
          <NotificationSettings s={s} setS={setS} />
        </Section>

        <Section title="Furniture Prices (Essential base, before HST)">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {s.furniture_prices.map((p, i) => (
              <div key={p.key} className="flex items-center justify-between border-b border-gray-100 pb-2">
                <span className="text-sm font-medium">{p.label}</span>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 text-sm">$</span>
                  <Input
                    data-testid={`price-${p.key}`}
                    type="number"
                    step="0.01"
                    defaultValue={dollars(p.price_cents)}
                    onChange={(e) => {
                      const fp = [...s.furniture_prices];
                      fp[i] = { ...p, price_cents: toCents(e.target.value) };
                      setS({ ...s, furniture_prices: fp });
                    }}
                    className="w-28 h-9 rounded-sm text-right"
                  />
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Package Upgrade Amounts">
          <div className="grid sm:grid-cols-2 gap-8">
            {["complete_upgrades", "signature_upgrades"].map((k) => (
              <div key={k}>
                <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500 mb-3">{k === "complete_upgrades" ? "Complete Upgrade" : "Signature Upgrade"}</p>
                {SIZES.map((size) => (
                  <div key={size} className="flex items-center justify-between py-1.5">
                    <span className="text-sm">{SIZE_LABELS[size]}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400 text-sm">+$</span>
                      <Input
                        data-testid={`${k}-${size}`}
                        type="number"
                        step="0.01"
                        defaultValue={dollars(s[k][size])}
                        onChange={(e) => setS({ ...s, [k]: { ...s[k], [size]: toCents(e.target.value) } })}
                        className="w-28 h-9 rounded-sm text-right"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Project Size Thresholds">
          <div className="grid sm:grid-cols-3 gap-5">
            <F label="Small: below ($)">
              <Input data-testid="threshold-small" type="number" defaultValue={dollars(s.small_max_cents)} onChange={(e) => setS({ ...s, small_max_cents: toCents(e.target.value) })} className="rounded-sm h-11" />
            </F>
            <F label="Medium: below ($)">
              <Input data-testid="threshold-medium" type="number" defaultValue={dollars(s.medium_max_cents)} onChange={(e) => setS({ ...s, medium_max_cents: toCents(e.target.value) })} className="rounded-sm h-11" />
            </F>
            <F label="Multi-Piece: min pieces">
              <Input data-testid="threshold-multi" type="number" value={s.multi_min_pieces} onChange={(e) => setS({ ...s, multi_min_pieces: parseInt(e.target.value) || 5 })} className="rounded-sm h-11" />
            </F>
          </div>
        </Section>

        <Section title="Tax, Deposit & Discount">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <F label="HST (%)"><Input data-testid="hst-rate" type="number" step="0.01" value={s.hst_rate} onChange={(e) => setS({ ...s, hst_rate: parseFloat(e.target.value) || 0 })} className="rounded-sm h-11" /></F>
            <F label="Deposit (%)"><Input data-testid="deposit-percent" type="number" step="0.01" value={s.deposit_percent} onChange={(e) => setS({ ...s, deposit_percent: parseFloat(e.target.value) || 0 })} className="rounded-sm h-11" /></F>
            <F label="Pay-in-Full Discount (%)"><Input data-testid="discount-percent" type="number" step="0.01" value={s.discount_percent} onChange={(e) => setS({ ...s, discount_percent: parseFloat(e.target.value) || 0 })} className="rounded-sm h-11" /></F>
            <F label="Quote Validity (days)"><Input data-testid="validity-days" type="number" value={s.quote_validity_days} onChange={(e) => setS({ ...s, quote_validity_days: parseInt(e.target.value) || 30 })} className="rounded-sm h-11" /></F>
          </div>
        </Section>

        <Section title="Package Copy">
          <div className="space-y-5">
            {["essential", "complete", "signature"].map((pkg) => (
              <div key={pkg} className="space-y-3">
                <F label={`${pkg} tagline`}>
                  <Input data-testid={`${pkg}-tagline`} value={s[`${pkg}_tagline`]} onChange={(e) => setS({ ...s, [`${pkg}_tagline`]: e.target.value })} className="rounded-sm h-11" />
                </F>
                <F label={`${pkg} inclusions (one per line)`}>
                  <Textarea data-testid={`${pkg}-includes`} rows={5} value={s[`${pkg}_includes`].join("\n")} onChange={(e) => setS({ ...s, [`${pkg}_includes`]: linesToArr(e.target.value) })} className="rounded-sm" />
                </F>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Landing Page Video (VSL) — hero of revival.arkfurniture.ca">
          <VslManager settings={s} />
        </Section>

        <Section title="Revival Showcase Videos (top of every customer quote)">
          <ShowcaseManager settings={s} />
        </Section>

        <Section title="Before & After Gallery (landing page + every customer quote)">
          <GalleryManager />
        </Section>

        <Section title="Landing Page Tracking">
          <div className="grid sm:grid-cols-2 gap-5">
            <F label="Meta Pixel ID">
              <Input data-testid="meta-pixel-id" placeholder="e.g. 823018949208258" value={s.meta_pixel_id || ""} onChange={(e) => setS({ ...s, meta_pixel_id: e.target.value.replace(/\D/g, "") })} className="rounded-sm h-11" />
            </F>
          </div>
          <p className="text-xs text-gray-400 mt-4 leading-relaxed">
            When a Pixel ID is set, the landing page fires PageView plus LandingPageView, VSLPlay, VSL25Percent, VSL50Percent,
            VSL75Percent, QualificationStarted, PhotoUploadCompleted and LeadSubmitted (also sent as the standard Meta "Lead" event).
            Leave blank to disable. UTM parameters and fbclid are always saved on each lead.
          </p>
        </Section>

        <Section title="Customer Page Copy">
          <div className="space-y-5">
            <F label="Resurfacing explanation">
              <Textarea data-testid="resurfacing-explanation" rows={4} value={s.resurfacing_explanation} onChange={(e) => setS({ ...s, resurfacing_explanation: e.target.value })} className="rounded-sm" />
            </F>
            <F label="Resurfacing colour note">
              <Textarea data-testid="resurfacing-note" rows={3} value={s.resurfacing_note} onChange={(e) => setS({ ...s, resurfacing_note: e.target.value })} className="rounded-sm" />
            </F>
            <F label="The ARK Resurfacing Promise">
              <Textarea data-testid="ark-promise" rows={4} value={s.ark_promise} onChange={(e) => setS({ ...s, ark_promise: e.target.value })} className="rounded-sm" />
            </F>
          </div>
        </Section>

        <Section title="Private Client Program (Realtor Partners)">
          <div className="grid sm:grid-cols-2 gap-5">
            <F label="Program name (shown everywhere)">
              <Input data-testid="program-name" value={s.program_name} onChange={(e) => setS({ ...s, program_name: e.target.value })} className="rounded-sm h-11" />
            </F>
            <F label="Client headline style">
              <Select value={s.client_headline_style} onValueChange={(v) => setS({ ...s, client_headline_style: v })}>
                <SelectTrigger data-testid="headline-style" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A — "Any friend of Joe Smith's is a friend of ours."</SelectItem>
                  <SelectItem value="B">B — "Joe takes care of his clients. So we do too."</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Call booking URL (optional)">
              <Input data-testid="booking-url" placeholder="https://calendly.com/..." value={s.booking_url} onChange={(e) => setS({ ...s, booking_url: e.target.value })} className="rounded-sm h-11" />
            </F>
            <F label="Estimated gross margin (%)">
              <Input data-testid="gross-margin" type="number" step="0.1" value={s.gross_margin_percent} onChange={(e) => setS({ ...s, gross_margin_percent: parseFloat(e.target.value) || 0 })} className="rounded-sm h-11" />
            </F>
            <F label="Outreach sender name">
              <Input data-testid="rep-name" value={s.ark_rep_name} onChange={(e) => setS({ ...s, ark_rep_name: e.target.value })} className="rounded-sm h-11" />
            </F>
            <F label="Outreach sender title">
              <Input data-testid="rep-title" value={s.ark_rep_title} onChange={(e) => setS({ ...s, ark_rep_title: e.target.value })} className="rounded-sm h-11" />
            </F>
          </div>
          <p className="text-xs text-gray-400 mt-5 leading-relaxed">
            Partner client pricing always delivers the Signature Revival service at the Essential Revival price, so it
            updates automatically whenever you change furniture prices or package upgrades above.
          </p>
        </Section>

        <Button
          data-testid="save-settings-btn"
          onClick={save}
          disabled={saving}
          className="w-full sm:w-auto h-12 px-10 bg-black hover:bg-black/85 text-white rounded-sm tracking-[0.15em] uppercase text-xs btn-lift"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </AdminLayout>
  );
}
